import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedFetch } from './useAuth'
import type { Role } from './useDiagramLinks'

export type Collaborator = {
  id: string
  kind: 'user' | 'guest'
  role: Role
  createdAt: string
  email?: string
  name?: string
}

export function useDiagramCollaborators(diagramId?: string) {
  const fetcher = useAuthenticatedFetch()
  const qc = useQueryClient()
  const enabled = Boolean(diagramId)

  const collaborators = useQuery({
    queryKey: ['diagram', diagramId, 'collaborators'],
    enabled,
    // Invite/role mutations invalidate this key; access revocations arrive
    // through the access-policy invalidation feed. Wall-clock staleness only
    // suppresses the remount refetch storm.
    staleTime: 60_000,
    queryFn: async (): Promise<Collaborator[]> => {
      const r = await fetcher(`/api/diagrams/${diagramId}/collaborators`)
      if (!r.ok) throw new Error('Failed to load collaborators')
      const j = await r.json()
      return (j.collaborators || []) as Collaborator[]
    },
  })

  const invite = useMutation({
    mutationFn: async (payload: { emails: string[]; role: Role }) => {
      const r = await fetcher(`/api/diagrams/${diagramId}/collaborators`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      if (!r.ok) throw new Error('Failed to invite collaborators')
    },
    onMutate: async (payload) => {
      // Cancel outgoing refetches
      await qc.cancelQueries({ queryKey: ['diagram', diagramId, 'collaborators'] })
      
      // Snapshot previous value
      const previous = qc.getQueryData<Collaborator[]>(['diagram', diagramId, 'collaborators'])
      
      // Optimistically add new collaborators
      const optimisticCollaborators: Collaborator[] = payload.emails.map(email => ({
        id: `temp-${email}-${Date.now()}-${Math.random()}`,
        kind: 'guest' as const,
        role: payload.role,
        createdAt: new Date().toISOString(),
        email,
        name: undefined, // Will be blank until server responds
      }))
      
      qc.setQueryData<Collaborator[]>(
        ['diagram', diagramId, 'collaborators'],
        (old = []) => [...old, ...optimisticCollaborators]
      )
      
      return { previous }
    },
    onError: (_err, _payload, context) => {
      // Rollback on error
      if (context?.previous) {
        qc.setQueryData(['diagram', diagramId, 'collaborators'], context.previous)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['diagram', diagramId, 'collaborators'] })
      qc.invalidateQueries({ queryKey: ['dashboard', 'shared-with-me'] })
    },
  })

  const update = useMutation({
    mutationFn: async (payload: { collaboratorId: string; role: Role }) => {
      const r = await fetcher(`/api/diagrams/${diagramId}/collaborators/${encodeURIComponent(payload.collaboratorId)}`, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ role: payload.role }),
      })
      if (!r.ok) throw new Error('Failed to update collaborator role')
    },
    onMutate: async (payload) => {
      // Cancel outgoing refetches
      await qc.cancelQueries({ queryKey: ['diagram', diagramId, 'collaborators'] })
      
      // Snapshot previous value
      const previous = qc.getQueryData<Collaborator[]>(['diagram', diagramId, 'collaborators'])
      
      // Optimistically update the role
      qc.setQueryData<Collaborator[]>(
        ['diagram', diagramId, 'collaborators'],
        (old = []) => old.map(c => 
          c.id === payload.collaboratorId 
            ? { ...c, role: payload.role }
            : c
        )
      )
      
      return { previous }
    },
    onError: (_err, _payload, context) => {
      // Rollback on error
      if (context?.previous) {
        qc.setQueryData(['diagram', diagramId, 'collaborators'], context.previous)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['diagram', diagramId, 'collaborators'] })
      qc.invalidateQueries({ queryKey: ['dashboard', 'shared-with-me'] })
    },
  })

  const remove = useMutation({
    mutationFn: async (collaboratorId: string) => {
      const r = await fetcher(`/api/diagrams/${diagramId}/collaborators/${encodeURIComponent(collaboratorId)}`, {
        method: 'DELETE',
      })
      if (!r.ok) throw new Error('Failed to remove collaborator')
    },
    onMutate: async (collaboratorId) => {
      // Cancel outgoing refetches
      await qc.cancelQueries({ queryKey: ['diagram', diagramId, 'collaborators'] })
      
      // Snapshot previous value
      const previous = qc.getQueryData<Collaborator[]>(['diagram', diagramId, 'collaborators'])
      
      // Optimistically remove the collaborator
      qc.setQueryData<Collaborator[]>(
        ['diagram', diagramId, 'collaborators'],
        (old = []) => old.filter(c => c.id !== collaboratorId)
      )
      
      return { previous }
    },
    onError: (_err, _collaboratorId, context) => {
      // Rollback on error
      if (context?.previous) {
        qc.setQueryData(['diagram', diagramId, 'collaborators'], context.previous)
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['diagram', diagramId, 'collaborators'] })
      qc.invalidateQueries({ queryKey: ['dashboard', 'shared-with-me'] })
    },
  })

  return { collaborators, invite, update, remove }
}
